'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu, X, Globe } from 'lucide-react';
import { m, AnimatePresence } from 'motion/react';
import { useLanguage } from '@/context/LanguageContext';
import LogoSvg from '@/components/LogoSvg';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, language, setLanguage } = useLanguage();
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'nav.services', href: '#services' },
    { key: 'nav.portfolio', href: '#portfolio' },
    { key: 'nav.process', href: '#process' },
    { key: 'nav.about', href: '#about' },
  ];

  const handleLanguageChange = (lang: 'pt' | 'en') => {
    if (lang === language) return;
    setIsChanging(true);
    setTimeout(() => {
      setLanguage(lang);
      setIsChanging(false);
    }, 400);
  };

  return (
    <>
      {/* Cool translation overlay effect */}
      <AnimatePresence>
        {isChanging && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-md flex items-center justify-center pointer-events-none"
          >
            <m.div
              initial={{ scale: 0.5, opacity: 0, rotate: -45 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 1.5, opacity: 0, rotate: 45 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="text-[#19ffb5]"
            >
              <Globe className="w-16 h-16 animate-pulse" />
            </m.div>
          </m.div>
        )}
      </AnimatePresence>

      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-500 ease-out ${scrolled ? 'bg-black/80 backdrop-blur-xl py-3 shadow-lg shadow-black/50' : 'bg-transparent py-6'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 group relative z-[60]">
              <LogoSvg className="w-auto h-6 lg:h-7 opacity-90 transition-all duration-300 group-hover:scale-105 group-hover:opacity-100" />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-10">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  href={item.href}
                  className="text-[13px] font-semibold tracking-widest uppercase text-gray-400 hover:text-white transition-colors relative group"
                >
                  {t(item.key)}
                  <span className="absolute -bottom-2 left-0 w-0 h-[2px] bg-gradient-to-r from-[#006546] to-[#19ffb5] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-8 z-[60]">
              {/* Language Toggle Minimalist */}
              <div className="flex items-center gap-3 text-xs font-semibold tracking-widest uppercase">
                <button
                  onClick={() => handleLanguageChange('pt')}
                  className={`transition-colors ${language === 'pt' ? 'text-white' : 'text-gray-600 hover:text-gray-300'
                    }`}
                >
                  PT
                </button>
                <div className="w-[1px] h-3 bg-white/20"></div>
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`transition-colors ${language === 'en' ? 'text-white' : 'text-gray-600 hover:text-gray-300'
                    }`}
                >
                  EN
                </button>
              </div>

              <Link
                href="#contact"
                className="px-7 py-3 text-sm font-semibold text-black rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_16px_rgba(25,255,181,0.2)] hover:shadow-[0_8px_24px_rgba(25,255,181,0.3)]"
                style={{ background: 'linear-gradient(135deg, #006546 0%, #19ffb5 100%)' }}
              >
                {t('nav.start')}
              </Link>
            </div>

            {/* Mobile Actions */}
            <div className="md:hidden flex items-center gap-3 relative z-[60]">
              {/* Language Toggle Mobile */}
              <div className="flex items-center bg-white/5 p-1 rounded-full border border-white/10 relative">
                <div
                  className="absolute inset-y-1 bg-[#006546] rounded-full transition-all duration-400"
                  style={{
                    width: 'calc(50% - 2px)',
                    left: language === 'pt' ? '2px' : 'calc(50%)',
                  }}
                />
                <button
                  onClick={() => handleLanguageChange('pt')}
                  className={`relative z-10 px-2 py-1 text-[11px] font-semibold rounded-full transition-colors ${language === 'pt' ? 'text-white' : 'text-gray-400'
                    }`}
                >
                  PT
                </button>
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`relative z-10 px-2 py-1 text-[11px] font-semibold rounded-full transition-colors ${language === 'en' ? 'text-white' : 'text-gray-400'
                    }`}
                >
                  EN
                </button>
              </div>

              <button
                className="text-white p-2.5 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle menu"
              >
                {isOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isOpen && (
            <m.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed inset-0 w-full h-[100dvh] bg-black/98 backdrop-blur-2xl md:hidden overflow-y-auto flex flex-col pt-32 px-8 pb-12 z-[40]"
            >
              <div className="flex flex-col gap-8 h-full justify-center pb-20">
                {navItems.map((item, i) => (
                  <m.div
                    key={item.key}
                    initial={{ x: -30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -30, opacity: 0 }}
                    transition={{ delay: i * 0.1, duration: 0.3 }}
                  >
                    <Link
                      href={item.href}
                      className="text-4xl font-semibold text-white/80 hover:text-white pb-2 block tracking-tight transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      {t(item.key)}
                    </Link>
                  </m.div>
                ))}

                <m.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 30, opacity: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="mt-6"
                >
                  <Link
                    href="#contact"
                    className="w-full flex items-center justify-center px-6 py-4 text-lg font-semibold text-black rounded-full mt-4 transition-all duration-300 shadow-[0_8px_32px_rgba(25,255,181,0.2)]"
                    style={{ background: 'linear-gradient(135deg, #006546 0%, #19ffb5 100%)' }}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('nav.start')}
                  </Link>
                </m.div>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
}
